import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useApp } from '@/components/AppLayout';
import {
  purchaseOrderApi,
  clientApi,
  templateConfigApi,
  type PurchaseOrder,
  type Client,
} from '@/lib/api';
import { DEFAULT_TEMPLATE_SETTINGS, type HeaderField } from '@/components/TemplateDesigner/types';
import { SendPurchaseOrderModal } from '@/components/Modals/SendPurchaseOrderModal';
import { getPurchaseOrderStatusConfig } from '@/lib/purchaseOrderStatus';
import { formatAmount } from '@/lib/currency';
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  MessageSquare,
  Mail,
  Send,
  CheckCircle,
  Archive,
  Trash2,
  Package,
  StickyNote,
  File,
  Building2,
  ChevronRight,
  ChevronDown,
  Link,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSendBlockModal } from '@/contexts/SendBlockModalContext';

export function ViewPurchaseOrder() {
  const { id } = useParams<{ id: string }>();
  const { nav } = useBusinessNav();
  const { currentBusiness, user, isViewOnly } = useApp();
  const { showEmailVerificationModal, showViewOnlyBlockModal } = useSendBlockModal();

  const [loading, setLoading] = useState(true);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [error, setError] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [vendor, setVendor] = useState<Client | null>(null);
  const [editingNumber, setEditingNumber] = useState(false);
  const [editNumberValue, setEditNumberValue] = useState('');
  const [savingNumber, setSavingNumber] = useState(false);
  const [numberError, setNumberError] = useState('');
  const numberInputRef = useRef<HTMLInputElement>(null);
  const [customFieldLabels, setCustomFieldLabels] = useState<Record<string, string>>({});

  const startEditingNumber = () => {
    if (!purchaseOrder || isViewOnly) return;
    setEditNumberValue(purchaseOrder.purchase_order_number);
    setNumberError('');
    setEditingNumber(true);
    setTimeout(() => numberInputRef.current?.select(), 0);
  };

  const saveNumber = async () => {
    if (!purchaseOrder || savingNumber) return;
    const trimmed = editNumberValue.trim();
    if (!trimmed || trimmed === purchaseOrder.purchase_order_number) {
      setEditingNumber(false);
      setNumberError('');
      return;
    }
    setSavingNumber(true);
    const response = await purchaseOrderApi.updateNumber(purchaseOrder.id, trimmed);
    setSavingNumber(false);
    if (response.data) {
      setPurchaseOrder(response.data);
      setEditingNumber(false);
      setNumberError('');
    } else {
      setNumberError(response.error || 'Failed to update');
    }
  };

  const cancelEditingNumber = () => {
    setEditingNumber(false);
    setNumberError('');
  };

  useEffect(() => {
    if (currentBusiness && id) {
      purchaseOrderApi.get(id).then((response) => {
        if (response.data) {
          setPurchaseOrder(response.data);
          setLoading(false);
        } else if (response.error) {
          setError(response.error);
          setLoading(false);
        }
      });
    }
  }, [currentBusiness, id]);

  useEffect(() => {
    if (currentBusiness) {
      templateConfigApi.get(currentBusiness.id).then((r) => {
        if (r.data?.settings) {
          const settings = { ...DEFAULT_TEMPLATE_SETTINGS, ...r.data.settings };
          const labels: Record<string, string> = {};
          for (const f of (settings.headerFields || []) as HeaderField[]) {
            if (f.enabled && f.isCustom && f.documentTypes.includes('purchase_order')) {
              labels[f.id] = f.name;
            }
          }
          setCustomFieldLabels(labels);
        }
      }).catch(() => {});
    }
  }, [currentBusiness]);

  // Lock body scroll when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch vendor client when send modal opens
  useEffect(() => {
    if (showSendModal && purchaseOrder?.vendor && !vendor) {
      clientApi.get(purchaseOrder.vendor).then((response) => {
        if (response.data) {
          setVendor(response.data);
        }
      });
    }
  }, [showSendModal, purchaseOrder?.vendor, vendor]);

  const handleOpenSendModal = () => {
    if (!user?.is_email_verified) {
      showEmailVerificationModal(user?.email || '');
      return;
    }
    setShowSendModal(true);
  };

  const handleSendFromModal = async (data: {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    message: string;
    cc_self: boolean;
  }) => {
    const response = await purchaseOrderApi.send(id!, {
      medium: 'email',
      ...data,
    });
    if (response.sendBlocked) {
      setShowSendModal(false);
      if (response.sendBlocked.code === 'email_not_verified') {
        showEmailVerificationModal(response.sendBlocked.email || '');
      } else {
        showViewOnlyBlockModal();
      }
      return;
    }
    if (response.data) {
      setPurchaseOrder(response.data);
      toast.success('Purchase order sent');
    } else if (response.error) {
      toast.error(response.error);
      throw new Error(response.error);
    }
  };

  const handleMarkAsSent = async () => {
    const response = await purchaseOrderApi.send(id!, {
      medium: 'marked_by_user',
    });
    if (response.sendBlocked) {
      if (response.sendBlocked.code === 'email_not_verified') {
        showEmailVerificationModal(response.sendBlocked.email || '');
      } else {
        showViewOnlyBlockModal();
      }
      return;
    }
    if (response.data) {
      setPurchaseOrder(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleAccept = async () => {
    const response = await purchaseOrderApi.accept(id!);
    if (response.data) {
      setPurchaseOrder(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleArchive = async () => {
    const response = await purchaseOrderApi.archive(id!);
    if (response.data) {
      setPurchaseOrder(response.data);
    } else if (response.error) {
      alert(response.error);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this purchase order? This action cannot be undone.')) {
      return;
    }

    const response = await purchaseOrderApi.delete(id!);
    if (response.error) {
      alert(response.error);
      return;
    }

    nav('/purchase-orders');
  };

  if (loading) return <div className="fixed inset-0 z-50 bg-background" />;

  if (!purchaseOrder) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center">
        <p className="text-destructive mb-4">Purchase order not found</p>
        <Button onClick={() => nav('/purchase-orders')}>Back to Purchase Orders</Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center">
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={() => nav('/purchase-orders')}>Back to Purchase Orders</Button>
      </div>
    );
  }

  const subtotal = parseFloat(purchaseOrder.subtotal);
  const totalMarkup = parseFloat(purchaseOrder.total_markup);
  const total = parseFloat(purchaseOrder.total_amount);
  const statusConfig = getPurchaseOrderStatusConfig(purchaseOrder.status, `PO #${purchaseOrder.purchase_order_number}`);

  const handleCopyLink = () => {
    if (!purchaseOrder) return;
    const url = `${window.location.origin}/purchase-order/${purchaseOrder.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const canShareLink = purchaseOrder.status === 'sent';

  return (
    <>
      <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Main Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
              <button
                onClick={() => nav('/purchase-orders')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Purchase Orders
              </button>
              {purchaseOrder.vendor && (
                <>
                  <span className="text-muted-foreground/50">·</span>
                  <button
                    onClick={() => nav(`/clients/${purchaseOrder.vendor}`)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {purchaseOrder.vendor_name}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              <div className="flex-1" />
              {canShareLink && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                >
                  <Link className="h-4 w-4 mr-1" />
                  Copy Link
                </Button>
              )}
              {!isViewOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => nav(`/purchase-orders/${id}/edit`)}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="max-w-3xl mx-auto space-y-6"
              >
                {/* Title and Status */}
                <div className="flex items-center gap-3">
                  {editingNumber ? (
                    <div className="flex flex-col gap-1 py-0.5">
                      <div className="flex items-center gap-1.5">
                        <Input
                          ref={numberInputRef}
                          value={editNumberValue}
                          onChange={(e) => { setEditNumberValue(e.target.value); setNumberError(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveNumber(); if (e.key === 'Escape') cancelEditingNumber(); }}
                          onBlur={saveNumber}
                          className={`h-9 text-lg w-64 ${numberError ? 'border-destructive' : ''}`}
                          disabled={savingNumber}
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={cancelEditingNumber}
                          className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                          title="Cancel"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                      {numberError && <p className="text-xs text-destructive">{numberError}</p>}
                    </div>
                  ) : (
                    <h1
                      className="text-2xl font-semibold flex items-center gap-2 cursor-pointer hover:text-primary/80 transition-colors group"
                      onClick={startEditingNumber}
                      title="Click to edit"
                    >
                      {purchaseOrder.purchase_order_number}
                      <Package className="h-5 w-5 text-purple-500" />
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h1>
                  )}
                  <Badge variant="outline" className={statusConfig.textColorClass}>
                    {statusConfig.label}
                  </Badge>
                </div>

                {/* Vendor Info */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="p-4 border rounded-lg bg-muted/30"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Vendor</p>
                        <p className="font-medium">{purchaseOrder.vendor_name || 'Unknown Vendor'}</p>
                        {purchaseOrder.vendor_company && (
                          <p className="text-sm text-muted-foreground">{purchaseOrder.vendor_company}</p>
                        )}
                      </div>
                    </div>
                    {purchaseOrder.vendor && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => nav(`/clients/${purchaseOrder.vendor}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>

                {/* PO Details */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="font-medium">
                      {purchaseOrder.issued_date
                        ? new Date(purchaseOrder.issued_date + 'T00:00:00').toLocaleDateString()
                        : 'Not set'}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="font-medium capitalize">{purchaseOrder.status.replace(/_/g, ' ')}</p>
                  </div>
                  {purchaseOrder.custom_field_values && Object.entries(purchaseOrder.custom_field_values).map(([fieldId, value]) => (
                    <div key={fieldId} className="p-4 border rounded-lg">
                      <p className="text-sm text-muted-foreground">{customFieldLabels[fieldId] || fieldId}</p>
                      <p className="font-medium">{value || '—'}</p>
                    </div>
                  ))}
                </motion.div>

                {/* Line Items */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Line Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    {purchaseOrder.line_items.map(([sectionName, items], sectionIndex) => (
                      <div key={sectionIndex}>
                        {sectionName && (
                          <div className="bg-purple-500/5 border-b px-4 py-2">
                            <span className="text-sm font-medium">{sectionName}</span>
                          </div>
                        )}
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr className="text-xs text-muted-foreground">
                              <th className="text-left px-4 py-2">Item</th>
                              <th className="text-right px-4 py-2 w-20">Qty</th>
                              <th className="text-right px-4 py-2 w-24">Rate</th>
                              <th className="text-right px-4 py-2 w-24">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, itemIndex) => (
                              <tr key={item.id || itemIndex} className="border-t">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-sm">{item.name || '—'}</div>
                                  {item.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {item.description}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                                <td className="px-4 py-3 text-right text-sm">
                                  {formatAmount(item.computed_rate ?? item.rate)}
                                </td>
                                <td className="px-4 py-3 text-right text-sm">
                                  {formatAmount(item.computed_amount ?? item.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Totals */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="border-t pt-4 space-y-2"
                >
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatAmount(subtotal)}</span>
                  </div>
                  {totalMarkup > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Markup</span>
                      <span>{formatAmount(totalMarkup)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span>{formatAmount(total)}</span>
                  </div>
                </motion.div>

              </motion.div>
            </div>
          </motion.div>

          {/* Right: Fixed Sidebar */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', damping: 25 }}
            className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
          >
            {/* Order Total Header */}
            <div className="px-5 py-6 border-b bg-purple-500/5">
              <p className="text-sm text-muted-foreground mb-1">Order Total</p>
              <p className="text-3xl font-bold">{formatAmount(total)}</p>
              {totalMarkup > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  (includes {formatAmount(totalMarkup)} markup)
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-5 space-y-5">
              {/* Vendor Address */}
              {purchaseOrder.vendor_address && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Vendor Address
                  </Label>
                  <p className="text-sm bg-background p-3 rounded-lg border">
                    {purchaseOrder.vendor_address}
                  </p>
                </div>
              )}

              {/* Attachments */}
              <div className="space-y-2">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <File className="h-4 w-4" />
                  Attachments
                </Label>
                {purchaseOrder.attachments && purchaseOrder.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {purchaseOrder.attachments.map((att, index) => (
                      <a
                        key={index}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded-lg bg-background hover:bg-muted/50 transition-colors"
                      >
                        <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate">{att.filename}</span>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No attachments</p>
                )}
              </div>

              {/* Note for Vendor */}
              {purchaseOrder.note_for_vendor && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <StickyNote className="h-4 w-4" />
                    Note for Vendor
                  </Label>
                  <p className="text-sm whitespace-pre-wrap bg-background p-3 rounded-lg border">
                    {purchaseOrder.note_for_vendor}
                  </p>
                </div>
              )}

              {/* Internal Notes */}
              {purchaseOrder.internal_notes && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Internal Notes</Label>
                  <p className="text-sm whitespace-pre-wrap bg-yellow-50 dark:bg-yellow-950/30 p-3 rounded-lg border border-yellow-200 dark:border-yellow-900">
                    {purchaseOrder.internal_notes}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t bg-background space-y-3">
              {!isViewOnly && (
                <>
                  {purchaseOrder.status === 'draft' && (
                    <Button onClick={handleOpenSendModal} className="w-full">
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </Button>
                  )}
                  {purchaseOrder.status === 'sent' && (
                    <Button
                      onClick={handleAccept}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark Accepted
                    </Button>
                  )}
                  {purchaseOrder.status === 'accepted' && (
                    <Button variant="outline" onClick={handleArchive} className="w-full">
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full">
                        More Actions
                        <ChevronDown className="h-4 w-4 ml-auto" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleOpenSendModal}>
                        <Mail className="h-4 w-4 mr-2" />
                        Send via Email
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleOpenSendModal}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send via Text
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleMarkAsSent}>
                        <Send className="h-4 w-4 mr-2" />
                        Mark as Sent
                      </DropdownMenuItem>
                      {purchaseOrder.status === 'sent' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleAccept}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Accepted
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleArchive}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <SendPurchaseOrderModal
        open={showSendModal}
        onOpenChange={setShowSendModal}
        purchaseOrder={purchaseOrder}
        vendor={vendor}
        business={currentBusiness}
        onSend={handleSendFromModal}
      />
    </>
  );
}
